import clsx from "clsx";
import { Settings2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useStore } from "@/lib/store";
import type { JobStatus } from "@/lib/types";

interface FilterButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
  fullWidth?: boolean;
}

function FilterButton({
  label,
  active,
  onClick,
  fullWidth = false,
}: FilterButtonProps): React.JSX.Element {
  return (
    <button
      onClick={onClick}
      className={clsx(
        "rounded-lg border px-2.5 py-1.5 text-[12px] font-medium transition-all",
        fullWidth && "w-full",
        active
          ? "border-cyber-cyan/45 bg-cyber-cyan/20 text-cyber-text"
          : "border-cyber-border/90 bg-cyber-surface/10 text-cyber-text/80 hover:border-cyber-cyan/35 hover:bg-cyber-surface/40 hover:text-cyber-text",
      )}
    >
      {label}
    </button>
  );
}

interface TabsProps {
  value: "all" | "mine";
  allCount?: number;
  onChange: (value: "all" | "mine") => void;
  compact?: boolean;
}

function MineTabs({ value, allCount, onChange, compact = false }: TabsProps): React.JSX.Element {
  return (
    <div
      className={clsx(
        "border-cyber-border/90 bg-cyber-surface/35 relative grid grid-cols-2 items-center border p-1",
        compact ? "h-7 rounded-full" : "h-10 w-full rounded-lg",
      )}
    >
      <button
        type="button"
        onClick={() => onChange("mine")}
        className={clsx(
          "relative z-10 inline-flex h-full items-center justify-center text-[12px] font-medium transition-all",
          compact ? "rounded-l-full px-2.5" : "flex-1 rounded-md px-3",
          value === "mine"
            ? "border-cyber-cyan/45 bg-cyber-cyan/20 text-cyber-text border"
            : "text-cyber-text/80 hover:bg-cyber-surface/40 hover:text-cyber-text border border-transparent bg-transparent",
        )}
      >
        {compact ? "My" : "My Chats"}
      </button>
      <button
        type="button"
        onClick={() => onChange("all")}
        className={clsx(
          "relative z-10 inline-flex h-full items-center justify-center text-[12px] font-medium transition-all",
          compact ? "rounded-r-full px-2.5" : "flex-1 rounded-md px-3",
          value === "all"
            ? "border-cyber-cyan/45 bg-cyber-cyan/20 text-cyber-text border"
            : "text-cyber-text/80 hover:bg-cyber-surface/40 hover:text-cyber-text border border-transparent bg-transparent",
        )}
      >
        <span className="inline-flex items-center gap-1">
          All
          {!compact ? (
            <span className="bg-cyber-surface/70 text-cyber-muted rounded-full px-1.5 py-0.5 text-[10px] leading-none">
              {typeof allCount === "number" ? allCount : "…"}
            </span>
          ) : null}
        </span>
      </button>
    </div>
  );
}

const STATUS_OPTIONS: { label: string; value: JobStatus | undefined }[] = [
  { label: "All", value: undefined },
  { label: "Pending", value: "pending" },
  { label: "Processing", value: "processing" },
  { label: "Completed", value: "completed" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Failed", value: "failed" },
];

const SOURCE_OPTIONS: { label: string; value: string | undefined }[] = [
  { label: "All", value: undefined },
  { label: "Web", value: "web" },
  { label: "Slack", value: "slack" },
  { label: "Linear", value: "linear" },
];

function SectionLabel({ children }: { children: string }): React.JSX.Element {
  return (
    <div className="text-cyber-text/65 mb-1 text-[10px] tracking-[0.18em] uppercase">
      {children}
    </div>
  );
}

interface SidebarFiltersProps {
  allCount?: number;
}

interface SidebarMineToggleProps {
  allCount?: number;
}

export function SidebarMineToggle({ allCount }: SidebarMineToggleProps): React.JSX.Element {
  const filters = useStore((state) => state.filters);
  const setFilter = useStore((state) => state.setFilter);
  const value = filters.mine ? "mine" : "all";
  void allCount;

  return (
    <div className="flex items-center gap-1">
      <FilterButton label="My" active={value === "mine"} onClick={() => setFilter("mine", true)} />
      <FilterButton
        label="All"
        active={value === "all"}
        onClick={() => setFilter("mine", undefined)}
      />
    </div>
  );
}

export function SidebarAdvancedFiltersTrigger({
  allCount,
}: SidebarFiltersProps): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const isAdmin = useStore((state) => state.user?.role === "admin");
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(
    null,
  );
  const filters = useStore((state) => state.filters);
  const setFilter = useStore((state) => state.setFilter);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const scrollLockTargetRef = useRef<HTMLElement | null>(null);
  const previousOverflowRef = useRef<string | null>(null);

  const hasAdvancedFilters = Boolean(
    filters.status || filters.source || filters.includeAutomated || filters.person,
  );
  const theme = useStore((state) => state.theme);
  const sidebarHoverSurfaceClass =
    theme === "base" ? "hover:bg-cyber-border/80" : "hover:bg-cyber-border/50";
  const sidebarActiveSurfaceClass = theme === "base" ? "bg-cyber-border/70" : "bg-cyber-border/60";

  useEffect(() => {
    if (!expanded) return;

    function updatePosition(): void {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const width = 320;
      const viewportPadding = 8;
      const left = Math.min(
        Math.max(viewportPadding, rect.left + rect.width / 2 - width / 2),
        window.innerWidth - width - viewportPadding,
      );

      setDropdownPosition({
        top: rect.bottom + 4,
        left,
      });
    }

    function handlePointerDown(event: PointerEvent): void {
      const target = event.target as Node;
      if (dropdownRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      if (expanded) {
        setExpanded(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        setExpanded(false);
      }
    }

    updatePosition();
    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [expanded]);

  useEffect(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    function findScrollableAncestor(element: HTMLElement): HTMLElement | null {
      let current: HTMLElement | null = element.parentElement;

      while (current) {
        const style = window.getComputedStyle(current);
        const overflowY = style.overflowY;
        const isScrollable =
          (overflowY === "auto" || overflowY === "scroll") &&
          current.scrollHeight > current.clientHeight;
        if (isScrollable) return current;
        current = current.parentElement;
      }

      return null;
    }

    if (expanded) {
      const target = findScrollableAncestor(trigger);
      if (!target) return;
      scrollLockTargetRef.current = target;
      previousOverflowRef.current = target.style.overflowY;
      target.style.overflowY = "hidden";
      return () => {
        target.style.overflowY = previousOverflowRef.current ?? "";
        scrollLockTargetRef.current = null;
        previousOverflowRef.current = null;
      };
    }

    const target = scrollLockTargetRef.current;
    if (!target) return;
    target.style.overflowY = previousOverflowRef.current ?? "";
    scrollLockTargetRef.current = null;
    previousOverflowRef.current = null;
  }, [expanded]);

  function clearAdvancedFilters(): void {
    setFilter("status", undefined);
    setFilter("source", undefined);
    setFilter("includeAutomated", undefined);
    setFilter("person", undefined);
  }

  const dropdownPanel = (
    <div
      ref={panelRef}
      className={clsx(
        "border-cyber-border bg-cyber-bg fixed z-[130] w-[320px] origin-top rounded-2xl border p-4 shadow-[0_18px_36px_rgba(0,0,0,0.28)] transition-all",
        expanded
          ? "translate-y-0 scale-100 opacity-100"
          : "pointer-events-none -translate-y-1 scale-[0.98] opacity-0",
      )}
      style={
        dropdownPosition
          ? {
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }
          : undefined
      }
    >
      <div className="space-y-3">
        <div>
          <SectionLabel>Chats</SectionLabel>
          <MineTabs
            value={filters.mine ? "mine" : "all"}
            allCount={allCount}
            onChange={(value) => setFilter("mine", value === "mine" ? true : undefined)}
          />
        </div>

        {isAdmin && (
          <label className="block">
            <SectionLabel>Person</SectionLabel>
            <input
              type="search"
              value={filters.person || ""}
              onChange={(event) => setFilter("person", event.target.value || undefined)}
              placeholder="Search name or email"
              className="border-cyber-border bg-cyber-surface/10 text-cyber-text placeholder:text-cyber-muted/65 focus:border-cyber-cyan/45 w-full rounded-lg border px-2.5 py-1.5 text-[12px] focus:outline-none"
            />
            <span className="text-cyber-muted mt-1 block text-[10px]">
              Matches the person who started the chat.
            </span>
          </label>
        )}

        <div>
          <SectionLabel>Status</SectionLabel>
          <div className="grid grid-cols-3 gap-2">
            {STATUS_OPTIONS.map((opt) => (
              <FilterButton
                key={opt.label}
                label={opt.label}
                active={filters.status === opt.value}
                onClick={() => setFilter("status", opt.value)}
                fullWidth
              />
            ))}
          </div>
        </div>

        <div>
          <SectionLabel>Activity</SectionLabel>
          <FilterButton
            label="Include circuit runs"
            active={Boolean(filters.includeAutomated)}
            onClick={() => setFilter("includeAutomated", !filters.includeAutomated)}
            fullWidth
          />
        </div>

        <div>
          <SectionLabel>Source</SectionLabel>
          <div className="grid grid-cols-2 gap-2">
            {SOURCE_OPTIONS.map((opt) => (
              <FilterButton
                key={opt.label}
                label={opt.label}
                active={filters.source === opt.value}
                onClick={() => setFilter("source", opt.value)}
                fullWidth
              />
            ))}
          </div>
        </div>

        {hasAdvancedFilters ? (
          <div className="border-cyber-border border-t pt-3">
            <button
              onClick={clearAdvancedFilters}
              className="text-cyber-red hover:text-cyber-red/80 text-[12px] transition-colors"
            >
              Clear filters
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className={clsx("relative isolate", expanded && "z-[120]")} ref={dropdownRef}>
      <button
        ref={triggerRef}
        onClick={() => setExpanded((open) => !open)}
        className={clsx(
          "text-cyber-text/75 hover:text-cyber-text/90 relative z-[121] inline-flex h-7 w-7 items-center justify-center rounded-full transition-all before:absolute before:-inset-1 before:rounded-full before:content-['']",
          sidebarHoverSurfaceClass,
          (expanded || hasAdvancedFilters) && clsx(sidebarActiveSurfaceClass, "text-cyber-text/75"),
        )}
        aria-label="Open filters"
        aria-expanded={expanded}
      >
        <Settings2 size={14} />
        {hasAdvancedFilters && (
          <span className="bg-cyber-cyan absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full" />
        )}
      </button>
      {typeof document !== "undefined" ? createPortal(dropdownPanel, document.body) : null}
    </div>
  );
}
